// Hooks for linking saved Views (dashboards) to a report as datasets.
import { useContext } from 'react'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { apiGet, apiPost, apiPatch, apiDelete } from 'api'
import AuthContext from 'context/auth-context'

const getReportDatasets = async (token, reportId) => {
  const url = `reports/${reportId}/report_datasets`
  const data = await apiGet({ url, token })
  return data
}

export function useReportDatasets({ reportId, options = {} }) {
  const token = useContext(AuthContext)
  const queryFn = () => getReportDatasets(token, reportId)
  const queryKey = [`reports/${reportId}/report_datasets`]
  return useQuery(queryKey, queryFn, { enabled: !!reportId, ...options })
}

const createReportDataset = async (token, reportId, params) => {
  const url = `reports/${reportId}/report_datasets`
  const objectName = 'report_dataset'
  const data = await apiPost({ url, token, objectName, params })
  return data
}

export function useCreateReportDataset({ reportId, options }) {
  const token = useContext(AuthContext)
  const queryClient = useQueryClient()
  return useMutation((params) => createReportDataset(token, reportId, params), {
    ...(options || {}),
    onSuccess: (...args) => {
      queryClient.invalidateQueries([`reports/${reportId}/report_datasets`])
      queryClient.invalidateQueries([`reports/${reportId}`])
      options?.onSuccess?.(...args)
    }
  })
}

const updateReportDataset = async (token, id, params) => {
  const url = `report_datasets/${id}`
  const objectName = 'report_dataset'
  const data = await apiPatch({ url, token, objectName, params })
  return data
}

export function useUpdateReportDataset({ reportId, options }) {
  const token = useContext(AuthContext)
  const queryClient = useQueryClient()
  return useMutation(({ id, ...params }) => updateReportDataset(token, id, params), {
    ...(options || {}),
    onSuccess: (...args) => {
      queryClient.invalidateQueries([`reports/${reportId}/report_datasets`])
      options?.onSuccess?.(...args)
    }
  })
}

const destroyReportDataset = async (token, id) => {
  const url = `report_datasets/${id}`
  await apiDelete({ url, token })
}

export function useDestroyReportDataset({ reportId, options }) {
  const token = useContext(AuthContext)
  const queryClient = useQueryClient()
  return useMutation((id) => destroyReportDataset(token, id), {
    ...(options || {}),
    onSuccess: (...args) => {
      queryClient.invalidateQueries([`reports/${reportId}/report_datasets`])
      queryClient.invalidateQueries([`reports/${reportId}`])
      options?.onSuccess?.(...args)
    }
  })
}
